import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Calculator, Sparkles, FileText, Pin, PenTool, Layers, Check, ChevronUp, ChevronDown, X as XIcon, GripVertical } from "lucide-react";
import { visibleForAI } from "../lib/uploadFilters";
import InlinePluginPicker from "./InlinePluginPicker";
import PluginPromptsPanel from "./PluginPromptsPanel";
import PromptPreviewDialog from "./PromptPreviewDialog";
import { useAuth } from "../lib/auth";
import { previewPluginFragment, listSelectableTemplates } from "../lib/api";
import { listSavedPrompts, createSavedPrompt, deleteSavedPrompt } from "../lib/api/files";
import { useFeatureFlags } from "../lib/feature_flags";
import { toast } from "sonner";

export default function EstimateDialog({
    open,
    onOpenChange,
    files = [],
    onSubmit,
    projectId,
}) {
    const flags = useFeatureFlags();
    // iter-170: plugin toggles now live inside this dialog. Owns its
    // own `activePluginIds` state — persistence still happens per-project
    // via the InlinePluginPicker (PUT /projects/:id/plugins), so the
    // same plugins reappear pre-selected on next open.
    const [activePluginIds, setActivePluginIds] = useState([]);
    // iter-40: template picker + scope/trade selection were removed.
    // Pricing scope is now driven by active plugins (toggled on the
    // Workspace floating bar) and templates aren't selected per-run.
    const scope = "overall";
    const [docMode, setDocMode] = useState("all");
    const [selectedDocs, setSelectedDocs] = useState(new Set());
    const [extraNotes, setExtraNotes] = useState("");
    const [autoPin, setAutoPin] = useState(false);
    const [drawTakeoff, setDrawTakeoff] = useState(false);
    // Profit is now applied only on the final report page via the live
    // "Add profit" slider (Cost-vs-Profit popup). The per-estimate 3-tier
    // pricing strategy below already covers up-front pricing aggression.
    const [pricingStrategy, setPricingStrategy] = useState("average");
    // iter-71: pull the 3 strategy labels + hints from the superadmin-
    // configurable endpoint so the names on screen always match the
    // prompts being sent to the AI. Falls back to sensible defaults on
    // network failure.
    const STRATEGY_FALLBACK = [
        { id: "competitive", label: "Cost", hint: "~5% below median — at-cost, lean but credible", tone: "emerald" },
        { id: "average", label: "Competitive", hint: "Regional median — realistic market rate", tone: "sky" },
        { id: "high", label: "Average", hint: "~10–15% ABOVE median — protective for risky jobs", tone: "amber" },
    ];
    const [strategyOptions, setStrategyOptions] = useState(STRATEGY_FALLBACK);

    // iter-79: optional pricing-template references attached to this run.
    // Multi-select across the user's own + workgroup-shared templates.
    const [selectableTemplates, setSelectableTemplates] = useState([]);
    const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
    const [templatesPickerOpen, setTemplatesPickerOpen] = useState(false);
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            try {
                const rows = await listSelectableTemplates();
                if (!cancelled) setSelectableTemplates(rows || []);
            } catch {
                /* non-fatal */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open]);
    // iter-43: per-plugin prompt state owned by the parent dialog.
    const [pluginPrompts, setPluginPrompts] = useState({
        checkedIds: [],
        adhoc: {},
    });
    // iter-48: superadmin dev-only prompt preview.
    const { user } = useAuth();
    const isSuperadmin = user?.role === "superadmin";
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewFragment, setPreviewFragment] = useState("");
    const [previewLoading, setPreviewLoading] = useState(false);
    const [pendingPayload, setPendingPayload] = useState(null);

    // iter-132: Saved-prompts dropdown at the top of the dialog. Users
    // can pick "Default" (admin-defined prompt for the pricing strategy)
    // or any of their saved prompts; when a saved prompt is picked its
    // text overrides the default. A Save button captures the current
    // textarea contents under a user-provided name and appends it to
    // the dropdown. The default entry is never deletable.
    const [savedPrompts, setSavedPrompts] = useState([]);
    const [selectedPromptId, setSelectedPromptId] = useState("default");
    const [promptText, setPromptText] = useState("");
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            try {
                const { prompts } = await listSavedPrompts();
                if (!cancelled) setSavedPrompts(prompts || []);
            } catch { /* non-fatal */ }
        })();
        return () => { cancelled = true; };
    }, [open]);
    useEffect(() => {
        if (selectedPromptId === "default") { setPromptText(""); return; }
        const sp = savedPrompts.find((p) => p.id === selectedPromptId);
        if (sp) setPromptText(sp.prompt || "");
    }, [selectedPromptId, savedPrompts]);
    const savePromptAs = async () => {
        const text = (promptText || "").trim();
        if (!text) { toast.error("Nothing to save — write a prompt first."); return; }
        // eslint-disable-next-line no-alert
        const name = window.prompt("Give this prompt a name:");
        if (!name) return;
        try {
            const created = await createSavedPrompt(name.trim(), text);
            setSavedPrompts((prev) => [created, ...prev]);
            setSelectedPromptId(created.id);
            toast.success("Prompt saved.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed.");
        }
    };
    const deletePrompt = async (pid) => {
        if (pid === "default") return;
        // eslint-disable-next-line no-alert
        if (!window.confirm("Delete this saved prompt?")) return;
        try {
            await deleteSavedPrompt(pid);
            setSavedPrompts((prev) => prev.filter((p) => p.id !== pid));
            if (selectedPromptId === pid) setSelectedPromptId("default");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed.");
        }
    };

    // iter-167 — Broadened from PDF-only to any visual document so
    // projects made of iPhone photos (HEIC), scans (PNG/JPG) etc.
    // unlock the Generate button too. Var name kept for minimal churn.
    const pdfs = visibleForAI(
        files.filter((f) => {
            const ct = (f.content_type || "").toLowerCase();
            const name = (f.original_filename || "").toLowerCase();
            if (ct === "application/pdf" || name.endsWith(".pdf")) return true;
            if (ct.startsWith("image/")) return true;
            return /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i.test(name);
        }),
    );

    useEffect(() => {
        if (!open) return;
        setExtraNotes("");
        setDocMode("all");
        setSelectedDocs(new Set());
        setAutoPin(false);
        setDrawTakeoff(false);
        setPluginPrompts({ checkedIds: [], adhoc: {} });
    }, [open]);

    // Fetch strategy labels/hints from the admin-customisable endpoint
    // once per dialog open. Silent fallback to hardcoded defaults.
    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const { listPricingStrategies } = await import("../lib/api");
                const data = await listPricingStrategies();
                const items = (data?.strategies || []).map((s, i) => ({
                    id: s.id,
                    label: s.label || STRATEGY_FALLBACK[i]?.label || s.id,
                    hint: s.hint || STRATEGY_FALLBACK[i]?.hint || "",
                    tone: ["emerald", "sky", "amber"][i] || "sky",
                }));
                if (items.length === 3) setStrategyOptions(items);
            } catch {
                /* keep defaults */
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);


    const toggleDoc = (id) => {
        setSelectedDocs((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const canSubmit =
        (pdfs.length > 0 && (docMode === "all" || selectedDocs.size > 0)) ||
        (pdfs.length === 0 && extraNotes.trim().length >= 10);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        const adhocList = Object.entries(pluginPrompts.adhoc || {})
            .map(([plugin_id, text]) => ({
                plugin_id,
                text: (text || "").trim(),
            }))
            .filter((e) => e.text && (activePluginIds || []).includes(e.plugin_id));
        const payload = {
            // Legacy template fields are left at their "don't-use" values
            // for backend back-compat. Plugin fragments now steer the
            // prompt instead. iter-79 reactivates `template_ids` so users
            // can attach 1+ templates as pricing references on a run.
            all_templates: false,
            template_ids: Array.isArray(selectedTemplateIds)
                ? selectedTemplateIds.filter(Boolean)
                : [],
            scope,
            trade: null,
            trades: [],
            plugin_ids: Array.from(activePluginIds || []),
            plugin_prompt_ids: pluginPrompts.checkedIds || [],
            plugin_adhoc_prompts: adhocList,
            plugin_prompt_inputs: pluginPrompts.inputs || {},
            document_ids: docMode === "specific" ? Array.from(selectedDocs) : null,
            extra_notes: (() => {
                // iter-132: if the user picked a saved prompt (or edited
                // the default in the textarea), inject it at the top of
                // extra_notes with a header so the backend prompt builder
                // uses it verbatim — no schema change required.
                const custom = (promptText || "").trim();
                const base = extraNotes.trim();
                if (selectedPromptId !== "default" && custom) {
                    return `PROMPT OVERRIDE:\n${custom}${base ? "\n\n" + base : ""}`;
                }
                return base;
            })(),
            auto_pin: flags.show_auto_pin ? autoPin : false,
            draw_takeoff: flags.show_draw_takeoff ? drawTakeoff : false,
            profit_margin_pct: 0,
            hide_profit_in_lines: false,
            pricing_strategy: pricingStrategy,
        };
        // iter-48: superadmin gets a prompt preview before the run.
        if (isSuperadmin) {
            setPreviewLoading(true);
            try {
                const { fragment } = await previewPluginFragment({
                    kind: "estimate",
                    plugin_ids: payload.plugin_ids,
                    plugin_prompt_ids: payload.plugin_prompt_ids,
                    plugin_adhoc_prompts: payload.plugin_adhoc_prompts,
                    plugin_prompt_inputs: payload.plugin_prompt_inputs,
                });
                setPreviewFragment(fragment || "");
                setPendingPayload(payload);
                setPreviewOpen(true);
            } catch (err) {
                toast.error(
                    err?.response?.data?.detail ||
                        "Couldn't build prompt preview — running anyway.",
                );
                onOpenChange(false);
                onSubmit && onSubmit(payload);
            } finally {
                setPreviewLoading(false);
            }
            return;
        }
        onOpenChange(false);
        onSubmit && onSubmit(payload);
    };

    const handlePreviewRun = () => {
        setPreviewOpen(false);
        onOpenChange(false);
        const p = pendingPayload;
        setPendingPayload(null);
        if (p) onSubmit && onSubmit(p);
    };
    const handlePreviewAbort = () => {
        setPreviewOpen(false);
        setPendingPayload(null);
        setPreviewFragment("");
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-[#141414] border border-white/15 text-white w-[calc(100vw-1.5rem)] max-w-[620px] max-h-[90vh] overflow-hidden p-4 sm:p-6"
                data-testid="estimate-dialog"
            >
                <div className="overflow-y-auto max-h-[calc(90vh-2rem)] min-w-0 flex flex-col gap-4">
                    <DialogHeader className="min-w-0">
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Calculator className="w-5 h-5 text-[#8fa4f0]" />
                            AI Estimate
                        </DialogTitle>
                        <DialogDescription className="text-slate-400">
                            Generate a line-itemized quote from the plans, priced against one of
                            your pricing templates — or skip the plans and describe the job in
                            words for a text-only quote.
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* iter-170: plugin picker at the top of the form. */}
                        <InlinePluginPicker
                            projectId={projectId}
                            onChange={setActivePluginIds}
                            testIdPrefix="estimate-plugin-picker"
                        />

                        {/* iter-132: Saved estimate prompts dropdown. */}
                        <section className="rounded-sm border border-white/15 bg-[#0a0a0a] p-3 space-y-2" data-testid="estimate-prompt-selector">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <Label className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-bold">
                                    Prompt
                                </Label>
                                <div className="flex items-center gap-1 flex-wrap">
                                    <select
                                        value={selectedPromptId}
                                        onChange={(e) => setSelectedPromptId(e.target.value)}
                                        data-testid="estimate-prompt-select"
                                        className="bg-[#141414] border border-white/15 text-white text-sm rounded px-2 py-1 max-w-[240px]"
                                    >
                                        <option value="default">Default (admin)</option>
                                        {savedPrompts.map((p) => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                    {selectedPromptId !== "default" && (
                                        <button
                                            type="button"
                                            onClick={() => deletePrompt(selectedPromptId)}
                                            data-testid="estimate-prompt-delete"
                                            title="Delete this saved prompt"
                                            className="p-1 rounded text-rose-400 hover:bg-rose-500/10"
                                        >
                                            <XIcon className="w-4 h-4" />
                                        </button>
                                    )}
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={savePromptAs}
                                        data-testid="estimate-prompt-save"
                                        className="border-white/15 text-white hover:bg-white/10 text-xs h-7"
                                    >
                                        Save as…
                                    </Button>
                                </div>
                            </div>
                            <Textarea
                                value={promptText}
                                onChange={(e) => setPromptText(e.target.value)}
                                placeholder={selectedPromptId === "default"
                                    ? "Uses the admin's default prompt for the selected pricing strategy. Type here to override — or pick a saved prompt above."
                                    : "Edit this prompt, then hit Save as… to store your changes under a new name."}
                                rows={4}
                                data-testid="estimate-prompt-textarea"
                                className="bg-[#141414] border-white/15 text-white text-xs font-mono resize-none"
                            />
                            {/* iter-133: live preview strip so users know
                                exactly what's about to hit the model. */}
                            <div className="flex items-center justify-between gap-2 pt-1 text-[10px] text-slate-400" data-testid="estimate-prompt-preview">
                                <span>
                                    {selectedPromptId === "default" && !promptText.trim() ? (
                                        <>Using the <strong className="text-slate-200">{pricingStrategy}</strong> default prompt from Admin → Pricing Strategies.</>
                                    ) : (
                                        <>Custom prompt will be sent verbatim to the AI (<span className="text-slate-200">{promptText.trim().length}</span> chars, ~<span className="text-slate-200">{Math.ceil(promptText.trim().length / 4)}</span> tokens).</>
                                    )}
                                </span>
                                {selectedPromptId !== "default" && promptText.trim() && (
                                    <span className="text-emerald-400 font-medium">Override active</span>
                                )}
                            </div>
                        </section>

                        {/* iter-79: pricing-template references (own + workgroup). */}
                        <TemplatesPicker
                            templates={selectableTemplates}
                            selectedIds={selectedTemplateIds}
                            onChange={setSelectedTemplateIds}
                            open={templatesPickerOpen}
                            setOpen={setTemplatesPickerOpen}
                        />

                        {/* iter-43: per-plugin custom prompt boxes. */}
                        <PluginPromptsPanel
                            activePluginIds={activePluginIds}
                            kind="estimate"
                            value={pluginPrompts}
                            onChange={setPluginPrompts}
                            testIdPrefix="estimate-plugin-prompts"
                        />

                        {/* Documents */}
                        <section>
                            <Label className="text-[10px] uppercase tracking-[0.14em] text-slate-300 font-bold block mb-2">
                                <FileText className="w-3 h-3 inline mr-1 text-[#8fa4f0]" />
                                Documents
                            </Label>
                            {pdfs.length === 0 ? (
                                <div
                                    className="rounded-sm border border-[#1a3fd1]/40 bg-[#1a3fd1]/10 px-3 py-2.5 text-[12px] leading-relaxed text-slate-200"
                                    data-testid="estimate-text-only-banner"
                                >
                                    <div className="font-bold text-[#8fa4f0] uppercase tracking-wider text-[10px] mb-1">
                                        Text-only quote
                                    </div>
                                    No plans uploaded. Describe the job below
                                    (scope, materials, quantities, location, etc.)
                                    and the AI will build the estimate from your
                                    description.
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-2 gap-2 mb-2">
                                        {["all", "specific"].map((m) => (
                                            <button
                                                key={m}
                                                type="button"
                                                onClick={() => setDocMode(m)}
                                                data-testid={`estimate-docmode-${m}`}
                                                className={`rounded-sm border px-3 py-2 text-left transition-colors ${
                                                    docMode === m
                                                        ? "border-[#1a3fd1]/50 bg-[#1a3fd1]/15 text-white"
                                                        : "border-white/15 hover:border-[#1a3fd1]/60 text-slate-300"
                                                }`}
                                            >
                                                <div className="text-sm font-semibold">
                                                    {m === "all" ? "All documents" : "Specific"}
                                                </div>
                                                <div className="text-[11px] text-slate-400">
                                                    {m === "all"
                                                        ? `${pdfs.length} PDFs`
                                                        : `${selectedDocs.size} selected`}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                    {docMode === "specific" && (
                                        <div
                                            className="max-h-40 overflow-y-auto rounded-sm border border-white/15 bg-[#0a0a0a] divide-y divide-white/10"
                                            data-testid="estimate-doc-list"
                                        >
                                            {pdfs.map((f) => (
                                                <label
                                                    key={f.id}
                                                    className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-white/10"
                                                >
                                                    <Checkbox
                                                        checked={selectedDocs.has(f.id)}
                                                        onCheckedChange={() => toggleDoc(f.id)}
                                                        data-testid={`estimate-doc-${f.id}`}
                                                        className="border-slate-500 data-[state=checked]:bg-[#1a3fd1] data-[state=checked]:border-[#1a3fd1]/50"
                                                    />
                                                    <span className="text-sm text-white truncate flex-1">
                                                        {f.original_filename}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </section>

                        {/* Notes */}
                        <section>
                            <Label className="text-[10px] uppercase tracking-[0.14em] text-slate-300 font-bold block mb-2">
                                {pdfs.length === 0
                                    ? "Describe the job (required for text-only quotes)"
                                    : "Extra notes (optional)"}
                            </Label>
                            <Textarea
                                rows={pdfs.length === 0 ? 5 : 2}
                                value={extraNotes}
                                onChange={(e) => setExtraNotes(e.target.value)}
                                placeholder={
                                    pdfs.length === 0
                                        ? "e.g. Install a 12'x16' concrete patio with stamped finish, includes 4\" prep base, rebar grid, broom-finish edges. South-facing backyard, easy truck access."
                                        : "e.g. Exclude final grading; add 10% contingency on all excavation"
                                }
                                data-testid="estimate-extra-notes"
                                className={`bg-[#0a0a0a] text-white placeholder:text-slate-500 focus-visible:ring-[#1a3fd1] ${
                                    pdfs.length === 0 &&
                                    extraNotes.trim().length < 10
                                        ? "border-[#1a3fd1]/60"
                                        : "border-white/15"
                                }`}
                            />
                            {pdfs.length === 0 && extraNotes.trim().length < 10 && (
                                <p
                                    className="text-[11px] text-slate-400 mt-1"
                                    data-testid="estimate-text-only-hint"
                                >
                                    Write at least a short sentence so the AI has something to work with.
                                </p>
                            )}
                        </section>

                        {/* Pricing strategy — competitive / average / high */}
                        <section className="rounded-sm border border-white/15 bg-[#0a0a0a] p-3 space-y-2">
                            <Label className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-bold block">
                                Pricing strategy
                            </Label>
                            <div
                                className="grid grid-cols-1 sm:grid-cols-3 gap-2"
                                role="radiogroup"
                                data-testid="estimate-pricing-strategy"
                            >
                                {strategyOptions.map((opt) => {
                                    const selected = pricingStrategy === opt.id;
                                    const border = selected
                                        ? opt.tone === "emerald"
                                            ? "border-emerald-400 bg-emerald-500/15"
                                            : opt.tone === "amber"
                                              ? "border-amber-400 bg-amber-500/15"
                                              : "border-[#1a3fd1]/50 bg-[#1a3fd1]/15"
                                        : "border-white/15 bg-[#0a0a0a] hover:border-slate-500";
                                    const labelTone = selected
                                        ? opt.tone === "emerald"
                                            ? "text-emerald-200"
                                            : opt.tone === "amber"
                                              ? "text-amber-200"
                                              : "text-[#8fa4f0]"
                                        : "text-white";
                                    return (
                                        <button
                                            key={opt.id}
                                            type="button"
                                            role="radio"
                                            aria-checked={selected}
                                            onClick={() => setPricingStrategy(opt.id)}
                                            data-testid={`estimate-pricing-${opt.id}`}
                                            className={`text-left p-2.5 rounded-sm border transition-colors min-w-0 ${border}`}
                                        >
                                            <div className={`text-sm font-bold break-words ${labelTone}`}>
                                                {opt.label}
                                            </div>
                                            <div className="text-[11px] text-slate-200 mt-1 leading-snug break-words hyphens-auto">
                                                {opt.hint}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </section>

                        <label
                            htmlFor="estimate-auto-pin"
                            className={`flex items-start gap-3 rounded-sm border border-white/15 bg-white/[0.05] p-3 cursor-pointer hover:border-[#1a3fd1]/50 transition-colors ${flags.show_auto_pin ? "" : "hidden"}`}
                        >
                            <Checkbox
                                id="estimate-auto-pin"
                                checked={autoPin}
                                onCheckedChange={(v) => setAutoPin(v === true)}
                                data-testid="estimate-auto-pin"
                                className="mt-0.5 border-white/15 data-[state=checked]:bg-[#1a3fd1] data-[state=checked]:border-[#1a3fd1]/50"
                            />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 text-sm font-semibold text-white">
                                    <Pin className="w-3.5 h-3.5 text-[#8fa4f0]" />
                                    Auto-pin applicable pages
                                </div>
                                <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">
                                    When the estimate finishes, every document used to build it (and its split pages) will be pinned to the top of your sidebar.
                                </p>
                            </div>
                        </label>

                        <label
                            htmlFor="estimate-draw-takeoff"
                            className={`flex items-start gap-3 rounded-sm border border-white/15 bg-white/[0.05] p-3 cursor-pointer hover:border-[#1a3fd1]/50 transition-colors ${flags.show_draw_takeoff ? "" : "hidden"}`}
                        >
                            <Checkbox
                                id="estimate-draw-takeoff"
                                checked={drawTakeoff}
                                onCheckedChange={(v) => setDrawTakeoff(v === true)}
                                data-testid="estimate-draw-takeoff"
                                className="mt-0.5 border-white/15 data-[state=checked]:bg-[#00C853] data-[state=checked]:border-[#00C853]"
                            />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 text-sm font-semibold text-white">
                                    <PenTool className="w-3.5 h-3.5 text-[#00C853]" />
                                    Draw Takeoff
                                    <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-sm bg-[#00C853]/15 border border-[#00C853]/30 text-[9px] uppercase tracking-wider font-bold text-[#00C853]">
                                        Beta
                                    </span>
                                </div>
                                <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">
                                    After the estimate finishes the AI will also draw areas, linear runs and counts directly on every page — PlanSwift-style. Pages with shapes get auto-pinned.
                                </p>
                            </div>
                        </label>

                        <div
                            className="mt-1 rounded-sm border border-amber-600 bg-amber-100 px-3 py-2 text-[11px] leading-relaxed text-amber-900"
                            data-testid="estimate-ai-disclaimer"
                        >
                            <strong className="text-amber-900">Heads up:</strong> AI can misread plans, miss items
                            or get prices wrong. <strong>Always review the estimate line-by-line
                            before sending it to a client</strong> — Steeltooth takes no
                            responsibility for losses caused by relying on AI output. See our
                            {" "}
                            <a
                                href="/terms#ai-disclaimer"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline underline-offset-2 hover:text-amber-950"
                                data-testid="estimate-ai-disclaimer-link"
                            >
                                Terms §4
                            </a>.
                        </div>

                        <div className="flex items-center justify-end gap-2 pt-1">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => onOpenChange(false)}
                                data-testid="btn-estimate-cancel"
                                className="text-slate-300 hover:text-white hover:bg-white/10"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={!canSubmit}
                                data-testid="btn-estimate-submit"
                                className="bg-[#1a3fd1] hover:bg-[#1a3fd1]/80 text-white font-bold disabled:opacity-40 disabled:bg-[#1a1a1a]"
                            >
                                <Sparkles className="w-4 h-4 mr-2" />
                                Generate estimate
                            </Button>
                        </div>
                    </form>
                </div>
            </DialogContent>
            <PromptPreviewDialog
                open={previewOpen}
                onOpenChange={setPreviewOpen}
                fragment={previewFragment}
                kind="estimate"
                activePluginIds={activePluginIds}
                onRun={handlePreviewRun}
                onAbort={handlePreviewAbort}
                loading={previewLoading}
            />
        </Dialog>
    );
}

/**
 * iter-79 — multi-select picker over the user's own + workgroup-shared
 * templates. Sections rendered as <fieldset>s for screen-reader clarity;
 * selected templates flow into the estimate run via `template_ids`.
 */
function TemplatesPicker({ templates, selectedIds, onChange, open, setOpen }) {
    const grouped = (templates || []).reduce(
        (acc, t) => {
            const key = t.source === "workgroup"
                ? `${t.workgroup_name || "Workgroup"} (shared)`
                : "My templates";
            (acc[key] ||= []).push(t);
            return acc;
        },
        {},
    );
    const byId = (templates || []).reduce((acc, t) => {
        acc[t.id] = t;
        return acc;
    }, {});
    const selectedSet = new Set(selectedIds || []);
    // Selected templates rendered in PRIORITY ORDER (the order the user
    // pinned them). Backend honours this order — top template's prices
    // win, lower templates fill in gaps, market rates are last resort.
    const selectedRows = (selectedIds || [])
        .map((id) => byId[id])
        .filter(Boolean);
    const toggle = (id) => {
        if (selectedSet.has(id)) {
            onChange((selectedIds || []).filter((x) => x !== id));
        } else {
            onChange([...(selectedIds || []), id]);
        }
    };
    const move = (id, dir) => {
        const list = [...(selectedIds || [])];
        const idx = list.indexOf(id);
        if (idx < 0) return;
        const target = idx + dir;
        if (target < 0 || target >= list.length) return;
        const [item] = list.splice(idx, 1);
        list.splice(target, 0, item);
        onChange(list);
    };
    const remove = (id) => onChange((selectedIds || []).filter((x) => x !== id));
    const clear = () => onChange([]);
    return (
        <section data-testid="estimate-templates-picker">
            <Label className="text-[10px] uppercase tracking-[0.14em] text-slate-300 font-bold mb-2 flex items-center gap-1">
                <Layers className="w-3 h-3 inline text-[#8fa4f0]" />
                Reference pricing templates
                <span className="text-slate-500 font-normal normal-case ml-1">
                    (optional · multi-select · drag to set priority)
                </span>
            </Label>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="estimate-templates-picker-toggle"
                className="w-full text-left px-3 py-2 rounded-sm bg-[#0a0a0a] border border-white/15 hover:border-[#1a3fd1]/60 flex items-center gap-2"
            >
                <span className="text-xs text-slate-200 flex-1 truncate">
                    {selectedRows.length === 0
                        ? "No templates attached — AI will infer market rates."
                        : selectedRows.length === 1
                            ? selectedRows[0].name
                            : `${selectedRows[0].name} → +${selectedRows.length - 1} more (in priority order)`}
                </span>
                {selectedRows.length > 0 && (
                    <span
                        className="text-[10px] tabular-nums px-1.5 py-0.5 rounded-full bg-[#1a3fd1]/20 text-[#8fa4f0] font-bold shrink-0"
                        data-testid="estimate-templates-picker-count"
                    >
                        {selectedRows.length}
                    </span>
                )}
                <span className="text-[10px] text-slate-400 shrink-0">
                    {open ? "Hide" : "Browse"}
                </span>
            </button>
            {/* Selected priority list — always visible when there are
                picks, even when the browse panel is collapsed, so users
                can reorder without re-opening the browse. */}
            {selectedRows.length > 0 && (
                <ol
                    className="mt-2 space-y-1"
                    data-testid="estimate-templates-priority-list"
                >
                    {selectedRows.map((t, i) => (
                        <SelectedTemplateRow
                            key={t.id}
                            template={t}
                            index={i}
                            total={selectedRows.length}
                            onUp={() => move(t.id, -1)}
                            onDown={() => move(t.id, 1)}
                            onRemove={() => remove(t.id)}
                            onReorder={(toIndex) => {
                                if (toIndex === i) return;
                                const list = [...(selectedIds || [])];
                                const [moved] = list.splice(i, 1);
                                list.splice(toIndex, 0, moved);
                                onChange(list);
                            }}
                        />
                    ))}
                </ol>
            )}
            {open && (
                <div
                    className="mt-2 p-2 rounded-sm bg-[#111] border border-white/15 max-h-72 overflow-y-auto space-y-3"
                    data-testid="estimate-templates-picker-panel"
                >
                    {Object.keys(grouped).length === 0 ? (
                        <p className="text-[11px] text-slate-500 italic px-1 py-2">
                            No templates available. Create one under Templates first.
                        </p>
                    ) : (
                        Object.entries(grouped).map(([groupName, items]) => (
                            <fieldset key={groupName} className="space-y-1">
                                <legend className="text-[9px] uppercase tracking-[0.16em] text-[#8fa4f0] font-bold mb-1">
                                    {groupName}
                                </legend>
                                {items.map((t) => {
                                    const checked = selectedSet.has(t.id);
                                    return (
                                        <label
                                            key={t.id}
                                            className={`flex items-center gap-2 px-2 py-1.5 rounded-sm cursor-pointer text-xs border ${
                                                checked
                                                    ? "border-[#1a3fd1]/50 bg-[#1a3fd1]/10 text-white"
                                                    : "border-transparent text-slate-200 hover:bg-white/10"
                                            }`}
                                            data-testid={`estimate-templates-picker-${t.id}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggle(t.id)}
                                                className="sr-only"
                                            />
                                            <span
                                                className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 ${
                                                    checked
                                                        ? "bg-[#1a3fd1] border-[#1a3fd1]/50"
                                                        : "border-slate-500"
                                                }`}
                                                aria-hidden="true"
                                            >
                                                {checked && (
                                                    <Check className="w-3 h-3 text-white" />
                                                )}
                                            </span>
                                            <span className="truncate flex-1">
                                                {t.name}
                                            </span>
                                            {checked && (
                                                <span
                                                    className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-[#1a3fd1]/20 text-[#8fa4f0] tabular-nums shrink-0"
                                                    data-testid={`estimate-templates-rank-${t.id}`}
                                                >
                                                    #{(selectedIds || []).indexOf(t.id) + 1}
                                                </span>
                                            )}
                                        </label>
                                    );
                                })}
                            </fieldset>
                        ))
                    )}
                    {(selectedIds || []).length > 0 && (
                        <button
                            type="button"
                            onClick={clear}
                            data-testid="estimate-templates-picker-clear"
                            className="text-[11px] text-slate-400 hover:text-white underline mt-1"
                        >
                            Clear selection
                        </button>
                    )}
                </div>
            )}
        </section>
    );
}

/**
 * One row in the selected-templates priority list. Drag-and-drop on
 * desktop (HTML5 DnD via grip handle), up/down buttons on touch /
 * mobile. The visible "#1, #2…" rank doubles as a constant reminder
 * that order matters for pricing precedence.
 */
function SelectedTemplateRow({
    template,
    index,
    total,
    onUp,
    onDown,
    onRemove,
    onReorder,
}) {
    const onDragStart = (e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", String(index));
    };
    const onDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };
    const onDrop = (e) => {
        e.preventDefault();
        const from = Number(e.dataTransfer.getData("text/plain"));
        if (Number.isFinite(from)) onReorder(index);
    };
    return (
        <li
            draggable
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDrop={onDrop}
            className="flex items-center gap-2 px-2 py-1.5 rounded-sm bg-[#0a0a0a] border border-white/15 hover:border-[#1a3fd1]/60 transition-colors group"
            data-testid={`estimate-templates-priority-${template.id}`}
        >
            <GripVertical
                className="w-3.5 h-3.5 text-slate-500 group-hover:text-slate-300 cursor-grab active:cursor-grabbing shrink-0"
                aria-hidden="true"
            />
            <span
                className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#1a3fd1]/20 text-[#8fa4f0] text-[10px] font-bold tabular-nums shrink-0"
                title={`Priority ${index + 1} of ${total}`}
            >
                {index + 1}
            </span>
            <span className="text-xs text-slate-100 truncate flex-1">
                {template.name}
            </span>
            {template.source === "workgroup" && (
                <span className="text-[9px] uppercase tracking-wider text-slate-500 shrink-0 hidden sm:inline">
                    {template.workgroup_name || "shared"}
                </span>
            )}
            <button
                type="button"
                onClick={onUp}
                disabled={index === 0}
                aria-label="Move up"
                data-testid={`estimate-templates-priority-up-${template.id}`}
                className="p-1 rounded-sm text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
            >
                <ChevronUp className="w-3 h-3" />
            </button>
            <button
                type="button"
                onClick={onDown}
                disabled={index === total - 1}
                aria-label="Move down"
                data-testid={`estimate-templates-priority-down-${template.id}`}
                className="p-1 rounded-sm text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
            >
                <ChevronDown className="w-3 h-3" />
            </button>
            <button
                type="button"
                onClick={onRemove}
                aria-label="Remove"
                data-testid={`estimate-templates-priority-remove-${template.id}`}
                className="p-1 rounded-sm text-slate-400 hover:text-red-300 hover:bg-red-500/15"
            >
                <XIcon className="w-3 h-3" />
            </button>
        </li>
    );
}
